import type { Battle } from "@loot-os/api-contract";
import { BattleResultsGrid } from "@loot-os/react";
import { useMemo } from "react";

import { useCurrency } from "../../lib/currency.js";
import { SeatName } from "./SeatName.js";

/**
 * What each seat has pulled so far, on `@loot-os/react`'s results grid: seats across, a card per
 * round that fills in as the round is shown. A round is shown for every seat once the page has
 * shown it (`throughRound`), and for the current round seat by seat as each reel lands
 * (`currentRound` + `settledLanes`), as boxed lights the cards up.
 */
export function BattleResults({
  battle,
  throughRound,
  currentRound,
  settledLanes,
  viewerEndUserId,
}: {
  battle: Battle;
  /** The last round whose results every seat shows; -1 for none. */
  throughRound: number;
  /** The round in progress, whose landed seats also show; -1 for none. */
  currentRound: number;
  settledLanes: ReadonlySet<number>;
  viewerEndUserId: string | null | undefined;
}) {
  const currency = useCurrency();
  const lanes = useMemo(
    () =>
      battle.seats.map((seat) => {
        const lastShown =
          currentRound > throughRound && settledLanes.has(seat.seatIndex)
            ? currentRound
            : throughRound;
        const items = Array.from({ length: battle.roundCount }, (_, round) =>
          round <= lastShown
            ? (battle.results[round]?.find((o) => o.seatIndex === seat.seatIndex)?.item ?? null)
            : null,
        );
        return {
          items,
          totalCents: items.reduce((sum, item) => sum + (item?.value ?? 0), 0),
        };
      }),
    [battle.seats, battle.results, battle.roundCount, throughRound, currentRound, settledLanes],
  );
  const roundPriceCents = useMemo(() => battle.rounds.map((r) => r.priceCents), [battle.rounds]);

  return (
    <BattleResultsGrid
      lanes={lanes}
      roundCount={battle.roundCount}
      roundPriceCents={roundPriceCents}
      currency={currency}
      renderSeatHeader={(seatIndex) => {
        const seat = battle.seats[seatIndex];
        return seat ? <SeatName seat={seat} viewerEndUserId={viewerEndUserId} /> : null;
      }}
    />
  );
}
